import { useEffect, useRef, useState } from "react";
import SVG from "../../../Components/SVG";
import { formatLeverage, normalizeAllowedLeverages, SELECTABLE_LEVERAGE_MODE } from "../../../helper/leverage";

export default function LeverageSelector({ mode, fixedLeverage, options, value, onChange, disabled = false }) {
  if (mode !== SELECTABLE_LEVERAGE_MODE) {
    return <div className="w-full px-2.5 py-1.5 rounded-lg text-sm flex items-center justify-between bg-zi-bg1-light dark:bg-zi-bg1-dark text-zi-text-light dark:text-zi-text-dark"><span className="opacity-50">اهرم معاملاتی</span><bdi dir="ltr" className="font-medium">{formatLeverage(fixedLeverage)}</bdi></div>;
  }

  return <SelectableLeverage options={options} value={value} onChange={onChange} disabled={disabled} />;
}

function SelectableLeverage({ options, value, onChange, disabled }) {
  const allowed = normalizeAllowedLeverages(options);
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef(null);
  const menuRef = useRef(null);
  const selectedOptionRef = useRef(null);
  const selected = allowed.includes(Number(value)) ? Number(value) : allowed[0];

  useEffect(() => {
    const closeOnOutsideClick = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) setIsOpen(false);
    };
    const closeOnEscape = (event) => {
      if (event.key === "Escape") setIsOpen(false);
    };
    document.addEventListener("mousedown", closeOnOutsideClick);
    document.addEventListener("keydown", closeOnEscape);
    return () => {
      document.removeEventListener("mousedown", closeOnOutsideClick);
      document.removeEventListener("keydown", closeOnEscape);
    };
  }, []);

  useEffect(() => {
    if (!isOpen) return undefined;

    const frame = requestAnimationFrame(() => {
      const menu = menuRef.current;
      const option = selectedOptionRef.current;
      if (!menu || !option) return;

      const centeredScrollTop =
        option.offsetTop - (menu.clientHeight - option.offsetHeight) / 2;
      const maxScrollTop = Math.max(0, menu.scrollHeight - menu.clientHeight);
      menu.scrollTop = Math.min(Math.max(0, centeredScrollTop), maxScrollTop);
    });

    return () => cancelAnimationFrame(frame);
  }, [isOpen, selected]);

  if (!allowed.length) return null;

  return (
    <div ref={containerRef} className="relative w-full">
      <button
        type="button"
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        disabled={disabled}
        onClick={() => setIsOpen((open) => !open)}
        className="relative w-full inline-flex items-center justify-between rounded-lg text-sm gap-2.5 bg-zi-bg1-light dark:bg-zi-bg1-dark text-zi-text-light dark:text-zi-text-dark py-1.5 px-2.5 outline-none cursor-pointer hocus:ring-1 hocus:ring-zi-border-dark disabled:cursor-not-allowed disabled:opacity-50"
      >
        <span className="text-zi-text-light/50 dark:text-zi-text-dark/50">اهرم</span>
        <span className="inline-flex items-center gap-2.5">
          <bdi dir="ltr">{formatLeverage(selected)}</bdi>
          <SVG name="select" classname={`size-3 stroke-zi-text-light dark:stroke-zi-text-dark ${isOpen ? "rotate-180" : ""}`} />
        </span>
      </button>

      {isOpen && (
        <div ref={menuRef} role="listbox" aria-label="اهرم معاملاتی" className="absolute inset-x-0 mt-2 z-50 max-h-64 overflow-auto rounded-2xl border border-zi-border-light/60 dark:border-zi-border-dark/60 bg-zi-bg2-light dark:bg-zi-bg2-dark shadow-lg">
          {allowed.map((leverage) => {
            const isActive = leverage === selected;
            return (
              <button
                key={leverage}
                ref={isActive ? selectedOptionRef : null}
                type="button"
                role="option"
                aria-selected={isActive}
                onClick={() => {
                  onChange?.(leverage);
                  setIsOpen(false);
                }}
                className={`w-full px-3 py-2 flex items-center gap-2.5 text-base [direction:ltr] text-zi-text-light dark:text-zi-text-dark hocus:bg-zi-bg1-light/50 dark:hocus:bg-zi-bg1-dark/50 ${isActive ? "bg-zi-bg1-light/50 dark:bg-zi-bg1-dark/50" : ""}`}
              >
                <span>{formatLeverage(leverage)}</span>
                {isActive && <SVG name="check" classname="size-4 stroke-zi-text-light dark:stroke-zi-text-dark" />}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
